import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { Building, MapPin, Phone, Save, User } from "lucide-react";
import React, { useEffect, useState } from "react";
import { countries } from "./data";

const EditCashCustomerList = ({
  handleCancel,
  handleSave,
  loading,
  formData,
  updateFormData,
  errors,
}) => {
  const [countriesList, setCountriesList] = useState([]);
  useEffect(() => {
    fetch("https://cdn.jsdelivr.net/npm/world-countries/countries.json")
      .then((res) => res.json())
      .then((data) => {
        const formatted = data
          .map((country) => {
            const root = country.idd?.root || "";
            const suffix = country.idd?.suffixes?.[0] || "";

            return {
              code: country.cca2.toLowerCase(),
              label: country.name.common,
              value: country.name.common,
              flag: `https://flagcdn.com/w40/${country.cca2.toLowerCase()}.png`,
              phoneCode: root + suffix, // "+91"
            };
          })
          .filter((c) => c.phoneCode) // remove weird edge cases
          .sort((a, b) => a.label.localeCompare(b.label));

        setCountriesList(formatted);
      });
  }, []);
  return (
    <div className="lg:p-3 p-2">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-5 py-4">
          <div className="flex lg:flex-row flex-col items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900">
              Edit Customer Details
            </h2>
            <div className="flex items-center justify-end space-x-3 ">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={handleSave}
                disabled={loading}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center"
              >
                <Save className="w-4 h-4 mr-2" />
                {loading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>

          <form className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 flex items-center mb-2">
                      <User className="w-4 h-4 mr-2" />
                      Personal Information
                    </h3>
                  </div>

                  <TextField
                    size="small"
                    fullWidth
                    label="Contact Name"
                    value={formData.contactName}
                    onChange={(e) =>
                      updateFormData("contactName", e.target.value)
                    }
                    error={!!errors.contactName}
                    helperText={errors.contactName}
                    required
                    variant="outlined"
                  />

                  <TextField
                    size="small"
                    fullWidth
                    label="Email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateFormData("email", e.target.value)}
                    error={!!errors.email}
                    helperText={errors.email}
                    variant="outlined"
                  />

                  <FormControl size="small" fullWidth>
                    <InputLabel>Patron Type</InputLabel>
                    <Select
                      value={formData.patronType}
                      onChange={(e) =>
                        updateFormData("patronType", e.target.value)
                      }
                      label="Patron Type"
                    >
                      <MenuItem value="Individual">Individual</MenuItem>
                      <MenuItem value="Corporate">Corporate</MenuItem>
                      <MenuItem value="Patron">Patron</MenuItem>
                      <MenuItem value="vehicle Owner">Vehicle Owner</MenuItem>
                    </Select>
                  </FormControl>
                </div>

                {/* Contact Information */}
                <div className="space-y-3">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 flex items-center mb-2">
                      <Phone className="w-4 h-4 mr-2" />
                      Contact Information
                    </h3>
                  </div>

                  {/* Primary Phone Row */}
                  <div className="grid grid-cols-3 gap-2">
                    <FormControl size="small">
                      <InputLabel>Code</InputLabel>
                      <Select
                        value={formData.primaryCountryCode}
                        onChange={(e) =>
                          updateFormData("primaryCountryCode", e.target.value)
                        }
                        label="Code"
                      >
                        {countriesList?.map((option) => (
                          <MenuItem key={option.code} value={option.phoneCode}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <img
                                src={option.flag}
                                alt={option.label}
                                width="20"
                                height="15"
                                style={{ marginRight: 8 }}
                              />
                              {option.phoneCode}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <div className="col-span-2">
                      <TextField
                        size="small"
                        fullWidth
                        label="Primary Phone*"
                        type="tel"
                        value={formData.primaryPhone}
                        onChange={(e) =>
                          updateFormData("primaryPhone", e.target.value)
                        }
                        variant="outlined"
                        error={!!errors.primaryPhone}
                        helperText={errors.primaryPhone}
                      />
                    </div>
                  </div>

                  {/* Alternate Phone Row */}
                  <div className="grid grid-cols-3 gap-2">
                    <FormControl size="small">
                      <InputLabel>Code</InputLabel>
                      <Select
                        value={formData.altCountryCode}
                        onChange={(e) =>
                          updateFormData("altCountryCode", e.target.value)
                        }
                        label="Code"
                      >
                        {countriesList?.map((option) => (
                          <MenuItem key={option.code} value={option.phoneCode}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <img
                                src={option.flag}
                                alt={option.label}
                                width="20"
                                height="15"
                                style={{ marginRight: 8 }}
                              />
                              {option.phoneCode}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <div className="col-span-2">
                      <TextField
                        size="small"
                        fullWidth
                        label="Alternate Phone"
                        type="tel"
                        value={formData.alternatePhone}
                        onChange={(e) =>
                          updateFormData("alternatePhone", e.target.value)
                        }
                        variant="outlined"
                      />
                    </div>
                  </div>

                  {/* WhatsApp Checkboxes */}
                  <div className="flex gap-4">
                    <label className="flex items-center text-sm">
                      <input
                        type="checkbox"
                        checked={formData.whatsappPrimary}
                        onChange={(e) =>
                          updateFormData("whatsappPrimary", e.target.checked)
                        }
                        className="mr-2"
                      />
                      Primary WhatsApp
                    </label>
                    <label className="flex items-center text-sm">
                      <input
                        type="checkbox"
                        checked={formData.whatsappAlternate}
                        onChange={(e) =>
                          updateFormData("whatsappAlternate", e.target.checked)
                        }
                        className="mr-2"
                      />
                      Alternate WhatsApp
                    </label>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                {/* Address Information */}
                <div className="space-y-3">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 flex items-center mb-2">
                      <MapPin className="w-4 h-4 mr-2" />
                      Address Information
                    </h3>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <TextField
                      size="small"
                      fullWidth
                      label="City"
                      value={formData.city}
                      onChange={(e) => updateFormData("city", e.target.value)}
                      variant="outlined"
                    />

                    <FormControl size="small" fullWidth>
                      <InputLabel>Country</InputLabel>
                      <Select
                        value={formData.country}
                        onChange={(e) =>
                          updateFormData("country", e.target.value)
                        }
                        label="Country"
                      >
                        {countriesList.map((country) => (
                          <MenuItem key={country.code} value={country.code}>
                            <img
                              src={country.flag}
                              alt={country.label}
                              width="20"
                              className="mr-2 inline-block align-middle"
                            />
                            {country.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>

                  <div className=" grid grid-cols-2 items-center gap-2">
                    <FormControl size="small" fullWidth>
                      <InputLabel>Territory</InputLabel>
                      <Select
                        value={formData.territory}
                        onChange={(e) =>
                          updateFormData("territory", e.target.value)
                        }
                        label="Territory"
                      >
                        <MenuItem value="Dubai">Dubai</MenuItem>
                        <MenuItem value="Abu Dhabi">Abu Dhabi</MenuItem>
                        <MenuItem value="Sharjah">Sharjah</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      fullWidth
                      label="Area Code"
                      value={formData.areaCode}
                      onChange={(e) =>
                        updateFormData("areaCode", e.target.value)
                      }
                      variant="outlined"
                    />
                  </div>
                  <TextField
                    size="small"
                    fullWidth
                    label="Street Address"
                    value={formData.streetAddress}
                    onChange={(e) =>
                      updateFormData("streetAddress", e.target.value)
                    }
                    variant="outlined"
                  />
                </div>

                {/* Business Information */}
                <div className="space-y-3">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 flex items-center mb-2">
                      <Building className="w-4 h-4 mr-2" />
                      Business Information
                    </h3>
                  </div>

                  <div className=" grid grid-cols-2 items-center gap-2">
                    <TextField
                      size="small"
                      fullWidth
                      label="Credit Limit"
                      type="number"
                      value={formData.creditLimit}
                      onChange={(e) =>
                        updateFormData("creditLimit", e.target.value)
                      }
                      error={!!errors.creditLimit}
                      helperText={errors.creditLimit}
                      variant="outlined"
                    />

                    <TextField
                      size="small"
                      fullWidth
                      label="Credit Expiry (Days)"
                      type="number"
                      value={formData.creditExpiry}
                      onChange={(e) =>
                        updateFormData("creditExpiry", e.target.value)
                      }
                      error={!!errors.creditExpiry}
                      helperText={errors.creditExpiry}
                      variant="outlined"
                    />
                  </div>

                  <TextField
                    size="small"
                    fullWidth
                    label="TRN"
                    value={formData.trn}
                    onChange={(e) => updateFormData("trn", e.target.value)}
                    variant="outlined"
                  />
                </div>

                {/* Comments */}
                <div>
                  <TextField
                    size="small"
                    fullWidth
                    label="Flash Comment"
                    multiline
                    rows={2}
                    value={formData.comment}
                    onChange={(e) => updateFormData("comment", e.target.value)}
                    placeholder="Flash comment..."
                    variant="outlined"
                  />
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditCashCustomerList;
